import { Box, type XCSS, xcss } from '@atlaskit/primitives';
import { DatabaseFieldType } from '@databases/types/database';
import React, { type FC, useEffect, useState, useRef, useMemo } from 'react';

import {
  FIELD_WIDTH_INLINE_THRESHOLD,
  MAX_POPOVER_EDIT_WIDTH,
  MIN_POPOVER_EDIT_WIDTH
} from './popover-edit-common/index.ts';

type PopoverEditProps = {
  isMultiSelect?: boolean;
  currentDataSection?: React.ReactNode;
  addDataSection: React.ReactNode;
  hasCurrentData: boolean;
  isRenderedInSidePanel?: boolean;
  isBulkEdit?: boolean;
  fieldWidth?: number;
  additionalXCSS?: XCSS;
  allowCurrentDataSectionClick?: boolean;
  fieldType?: string;
  suppressReferenceCellBorder?: boolean;
};

const ReferenceCellStyles = xcss({
  display: 'block',
  position: 'relative',
  height: '100%'
});

const ReferenceCellBorderStyles = xcss({
  borderWidth: 'border.width.focused',
  borderColor: 'color.border.focused',
  borderStyle: 'solid',
  borderRadius: 'radius.small'
});

const DefaultReferenceCellStyles = xcss({
  borderColor: 'color.border.input'
});

const BulkEditReferenceCellStyles = xcss({
  display: 'block',
  position: 'relative',
  height: '100%'
});

const PopoverContainerStyles = xcss({
  display: 'block',
  position: 'absolute',
  width: '100%',
  zIndex: 'modal',
  boxShadow: 'elevation.shadow.overlay',
  borderRadius: 'radius.small',
  minWidth: `${MAX_POPOVER_EDIT_WIDTH}px`,
  maxWidth: `${MAX_POPOVER_EDIT_WIDTH}px`
});

const popoverContainerMinWidthStyles = (minWidth: number) => {
  return xcss({
    minWidth: `${minWidth}px`
  });
};

const BulkEditPopoverContainerStyles = xcss({
  position: 'relative',
  left: 'space.0',
  right: 'space.0',
  bottom: 'space.0',
  top: 'space.0'
});

const PopoverContainerOffsetRightStyles = xcss({
  right: 'space.100'
});

const PopoverContainerOffsetLeftStyles = xcss({
  left: 'space.100'
});

const PopoverContainerTopStyles = xcss({
  bottom: 'space.100'
});

const PopoverContainerBottomStyles = xcss({
  top: 'space.100'
});

const PopoverContainerFilterViewStyles = xcss({
  minWidth: 'auto !important',
  position: 'relative',
  top: '0',
  right: '0',
  left: '0',
  width: '100%'
});

const CurrentDataStyles = xcss({
  backgroundColor: 'elevation.surface.sunken',
  borderRadius: 'inherit',
  padding: 'space.100'
});

const MultiSelectCurrentDataContainerStyles = xcss({
  borderBottomColor: 'color.border',
  borderBottomWidth: 'border.width',
  borderBottomStyle: 'solid',
  borderBottomLeftRadius: '0',
  borderBottomRightRadius: '0'
});

const AddDataStyles = xcss({
  backgroundColor: 'color.background.input',
  borderRadius: 'inherit',
  padding: '0'
});

const AddDataLimitHeightStyles = (maxHeight: number | null) => {
  return xcss({
    maxHeight: maxHeight ? `${Math.max(164, maxHeight)}px` : 'none' // 164 is roughly the height of 2 entries in a Select menu
  });
};

const MultiSelectAddDataContainerStyles = xcss({
  borderTopLeftRadius: '0',
  borderTopRightRadius: '0'
});

const InlineWidthStyles = xcss({
  maxWidth: 'none',
  minWidth: 'none',
  width: '100%',
  top: '0',
  right: '0',
  left: '0'
});

const updatePopupPosition = (
  popoverElement: HTMLElement,
  setHorizontalPosition: React.Dispatch<React.SetStateAction<string>>,
  setVerticalPosition: React.Dispatch<React.SetStateAction<string>>,
  setMaxAddDataHeight: React.Dispatch<React.SetStateAction<number | null>>
) => {
  const tableContainer = document.getElementById('scrollable-inner-container');

  if (tableContainer) {
    const popoverRect = popoverElement.getBoundingClientRect();
    const containerRect = tableContainer.getBoundingClientRect();

    // Check horizontal overflow
    if (popoverRect.right >= containerRect.right) {
      setHorizontalPosition('right');
    }

    // Handle vertical overflow
    const scrollableInnerContainerMidpointYCoordinate =
      (containerRect.top + containerRect.bottom) / 2;
    if (popoverRect.y >= scrollableInnerContainerMidpointYCoordinate) {
      // renders popover upwards
      setMaxAddDataHeight(popoverRect.y - containerRect.top);
      setVerticalPosition('top');
    } else {
      // render popover downwards
      setMaxAddDataHeight(containerRect.bottom - popoverRect.y);
      setVerticalPosition('bottom');
    }
  }
};

export const PopoverEdit: FC<PopoverEditProps> = ({
  isMultiSelect,
  currentDataSection,
  hasCurrentData,
  addDataSection,
  isRenderedInSidePanel,
  isBulkEdit,
  fieldWidth,
  additionalXCSS,
  allowCurrentDataSectionClick,
  fieldType,
  suppressReferenceCellBorder
}) => {
  // TODO - We should introduce a more flexible position for the popover
  const [horizontalPosition, setHorizontalPosition] = useState('left');
  const [verticalPosition, setVerticalPosition] = useState('bottom');
  const [maxAddDataHeight, setMaxAddDataHeight] = useState<number | null>(null);
  const isWidthInline = fieldWidth ? fieldWidth > FIELD_WIDTH_INLINE_THRESHOLD : false;

  const popoverRef = useRef(null);

  useEffect(() => {
    const popoverElement = popoverRef.current;
    if (!popoverElement) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      updatePopupPosition(
        popoverElement,
        setHorizontalPosition,
        setVerticalPosition,
        setMaxAddDataHeight
      );
      resizeObserver.disconnect();
    });

    resizeObserver.observe(popoverElement);

    return () => {
      // Disconnecting here to avoid memory leaks in the event
      // the component prematurely unmounts.
      resizeObserver.disconnect();
    };
  }, [hasCurrentData]); // resize when current data is updated

  const clickDisabledCurrentDataSection = useMemo(() => {
    return (
      <Box
        // Prevent closing the popover by
        // clicking current data section
        onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
          e.preventDefault();
        }}
      >
        {currentDataSection}
      </Box>
    );
  }, [currentDataSection]);

  const currentDataSectionToRender = allowCurrentDataSectionClick
    ? currentDataSection
    : clickDisabledCurrentDataSection;

  const currentDataSectionId = isMultiSelect
    ? 'popover-edit-container-multi-select-value'
    : 'popover-edit-container-value-currentData';

  const popoverSections = useMemo(() => {
    return (
      <>
        {hasCurrentData && (
          <Box
            xcss={[CurrentDataStyles, MultiSelectCurrentDataContainerStyles]}
            testId={currentDataSectionId}
            id={currentDataSectionId}
          >
            {currentDataSectionToRender}
          </Box>
        )}
        <Box
          xcss={[
            AddDataStyles,
            AddDataLimitHeightStyles(maxAddDataHeight),
            hasCurrentData && MultiSelectAddDataContainerStyles
          ]}
          testId="popover-edit-container-value-addData"
        >
          {addDataSection}
        </Box>
      </>
    );
  }, [
    hasCurrentData,
    currentDataSectionId,
    currentDataSectionToRender,
    maxAddDataHeight,
    addDataSection
  ]);

  const popover = useMemo(() => {
    return (
      <Box
        testId="popover-edit-container"
        ref={popoverRef}
        xcss={[
          PopoverContainerStyles,
          popoverContainerMinWidthStyles(
            fieldType && fieldType === DatabaseFieldType.ATTACHMENT
              ? MAX_POPOVER_EDIT_WIDTH
              : MIN_POPOVER_EDIT_WIDTH
          ),
          horizontalPosition === 'right'
            ? PopoverContainerOffsetRightStyles
            : PopoverContainerOffsetLeftStyles,
          verticalPosition === 'top'
            ? PopoverContainerTopStyles
            : PopoverContainerBottomStyles,
          isRenderedInSidePanel && PopoverContainerFilterViewStyles,
          isWidthInline && InlineWidthStyles,
          isBulkEdit && BulkEditPopoverContainerStyles,
          additionalXCSS
        ]}
      >
        {popoverSections}
      </Box>
    );
  }, [
    fieldType,
    horizontalPosition,
    verticalPosition,
    isRenderedInSidePanel,
    isWidthInline,
    isBulkEdit,
    additionalXCSS,
    popoverSections
  ]);

  return (
    <Box
      xcss={[
        ReferenceCellStyles,
        isBulkEdit
          ? BulkEditReferenceCellStyles
          : suppressReferenceCellBorder
            ? undefined
            : ReferenceCellBorderStyles,
        isRenderedInSidePanel && DefaultReferenceCellStyles
      ]}
    >
      {isRenderedInSidePanel ? popoverSections : popover}
    </Box>
  );
};
